import { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import './App.css';
import type { ManagerRole } from './api/managerApi';
import { SESSION_EXPIRED_EVENT, consumeSessionExpiredMessage } from './api/client';
import { canAccessRoute } from './auth/permissions';
import { LoadingState } from './components/common';
import Header from './components/header/Header';
import GlobalSidebar from './components/sidebar/GlobalSidebar';
import Sidebar from './components/sidebar/Sidebar';
import PageRouter from './pages/PageRouter';
import {
  DEFAULT_ROUTE_PATH,
  EVENT_LIST_ROUTE_PATH,
  LOGIN_ROUTE_PATH,
  getRouteByPath,
} from './routes/appRoutes';
import { isAuthenticated } from './services/authService';
import { getStoredManagerRole, loadMyManagerProfile } from './services/managerService';

function getLegacyHashRoute(hash: string) {
  if (!hash.startsWith('#/') || hash === '#/') {
    return null;
  }

  return hash.slice(1);
}

function redirectToLoginAfterSessionExpired() {
  globalThis.location.replace(`${LOGIN_ROUTE_PATH}?reason=session_expired`);
}

function App() {
  const location = useLocation();
  const legacyHashRoute = getLegacyHashRoute(location.hash);
  const activeRoute = getRouteByPath(location.pathname);
  const authenticated = isAuthenticated();
  const [role, setRole] = useState<ManagerRole | null>(() => getStoredManagerRole());
  const [roleLoading, setRoleLoading] = useState(false);
  const [sessionExpiredMessage, setSessionExpiredMessage] = useState('');

  useEffect(() => {
    const openSessionExpiredDialog = () => {
      setSessionExpiredMessage(consumeSessionExpiredMessage() ?? '다시 로그인해야 합니다.');
    };

    globalThis.addEventListener(SESSION_EXPIRED_EVENT, openSessionExpiredDialog);

    return () => {
      globalThis.removeEventListener(SESSION_EXPIRED_EVENT, openSessionExpiredDialog);
    };
  }, []);

  useEffect(() => {
    if (!authenticated) {
      setRole(null);
      setRoleLoading(false);
      return;
    }

    let isMounted = true;
    setRoleLoading(true);

    loadMyManagerProfile()
      .then((profile) => {
        if (isMounted) {
          setRole(profile.role);
        }
      })
      .catch(() => {
        if (isMounted) {
          setRole((currentRole) => currentRole ?? getStoredManagerRole());
        }
      })
      .finally(() => {
        if (isMounted) {
          setRoleLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [authenticated]);

  if (legacyHashRoute) {
    return <Navigate to={legacyHashRoute} replace />;
  }

  if (sessionExpiredMessage) {
    return (
      <Dialog
        fullWidth
        maxWidth="xs"
        onClose={redirectToLoginAfterSessionExpired}
        open
      >
        <DialogTitle sx={{ fontSize: 20, fontWeight: 800 }}>로그인이 필요합니다</DialogTitle>
        <DialogContent dividers sx={{ color: '#334155', fontSize: 14, lineHeight: 1.7 }}>
          {sessionExpiredMessage}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={redirectToLoginAfterSessionExpired} variant="contained">
            확인
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  if (!activeRoute) {
    return <Navigate to={DEFAULT_ROUTE_PATH} replace />;
  }

  if (activeRoute.section !== 'auth' && !authenticated) {
    return <Navigate to={LOGIN_ROUTE_PATH} replace />;
  }

  if (
    activeRoute.section === 'auth' &&
    activeRoute.id !== 'password-reset' &&
    activeRoute.id !== 'signup-password' &&
    authenticated
  ) {
    return <Navigate to={EVENT_LIST_ROUTE_PATH} replace />;
  }

  if (activeRoute.section === 'auth') {
    return <PageRouter route={activeRoute} />;
  }

  if (roleLoading) {
    return <LoadingState />;
  }

  if (!canAccessRoute(activeRoute, role)) {
    return <Navigate to={EVENT_LIST_ROUTE_PATH} replace />;
  }

  if (activeRoute.standalone) {
    return (
      <div className="dashboard">
        <Header standalone />
        <div className="main-shell">
          <GlobalSidebar activePath={activeRoute.path} role={role} />
          <main className="workspace" aria-label="전역 관리 작업 영역">
            <PageRouter route={activeRoute} />
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard">
      <Header />
      <div className="main-shell">
        <Sidebar activePath={activeRoute.path} role={role} />
        <main className="workspace" aria-label="대시보드 작업 영역">
          <PageRouter route={activeRoute} />
        </main>
      </div>
    </div>
  );
}

export default App;
